/**
 * Trending image-ad library — flat catalogue of high-performing example ads
 * grouped by visual format. Thumbnails are mirrored to our CDN under
 * `gs://cdn.wiserstudio.ai/static/ad-image-library/<category-slug>/<uuid>.webp`
 * (uploaded from `Static ads-Templates/` reference set). Categories drive the
 * chip filters and masonry grid in `StaticAdsPage`.
 */
import type { ProductImageAdTypeId } from "./imageAdTypes";

/** CDN root for the mirrored library thumbnails. Append
 *  `<category-slug>/<uuid>.webp` to resolve a single image. */
export const IMAGE_AD_LIBRARY_CDN_BASE =
  "https://cdn.wiserstudio.ai/static/ad-image-library";

export interface ImageAdLibraryCategory {
  id: string;
  label: string;
  description: string;
  /** Image-ad template id this category dispatches as. Each library category
   *  has a 1:1 dedicated entry in `LIBRARY_IMAGE_AD_TEMPLATES` (id matches
   *  `category.id`) so the dispatch brief carries the matching format title +
   *  2-line description as context for the reference image, instead of being
   *  collapsed into a generic trending template. */
  imageTypeId: ProductImageAdTypeId;
  images: readonly string[];
}

const HIDDEN_IMAGE_AD_LIBRARY_CATEGORY_IDS = new Set(["ugc-overlay", "interface"]);

const IMAGE_AD_LIBRARY_SOURCE = [
  {
    id: "before-and-after",
    label: "Before and After",
    description: "Transformation reveal showing the customer's pain on one side and the post-product result on the other.",
    imageTypeId: "before-and-after",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/8e044246-14bc-4072-990e-3217921f78c6.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/74da816d-ef0d-4b89-a74c-942cb23179b3.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/1e415591-4b2d-46c2-8dbf-c3c8f417f42e.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/5ee92243-56a4-4617-971c-d081fbb62f50.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/94b8b277-8219-41fd-a407-e367561759f0.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/5734edc3-85d1-4361-b7bd-92f3cd2bfbeb.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/c6ba2069-5633-44e0-9bcd-336a52c33c2a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/8fa0f9a9-3058-4a20-872b-4e4ad72815f5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/27e0ad7d-f625-4a3f-969d-123751cdfc2d.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/af729e45-3ac3-4bcc-993c-9a1df51e2ac0.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/before-and-after/300b0914-95e3-4d55-ab19-c9928d7b96d3.webp"
    ],
  },
  {
    id: "us-vs-them",
    label: "Us vs Them",
    description: "Side-by-side competitive comparison that frames your product as the obvious upgrade.",
    imageTypeId: "us-vs-them",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/059bf73b-ba56-4098-966e-cd244cd0e03a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/345c9a78-f95a-4667-93df-6c17147f4fa7.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/705500f9-6eb0-4aba-b0bd-3ae16b2178ae.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/9910f90d-17fb-4ef9-8f91-3c287540f082.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/414cef95-6f82-4913-a8be-1cec064dbe7e.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/243ad836-e0f7-4ec7-a768-bd304d7e19c4.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/38bbe828-d82d-4fc5-b010-0e3bc02aaf83.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/5f42a609-8294-48f6-994c-16e7a0bf1050.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/us-vs-them/4b87ab80-4651-4767-91e0-bd17fcaed0fc.webp"
    ],
  },
  {
    id: "listicle",
    label: "Listicle",
    description: "Numbered or bulleted layout that walks the viewer through reasons, features, or steps in seconds.",
    imageTypeId: "listicle",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/1f117043-7e4c-404a-9185-88867843f2f4.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/91a03599-93ab-4b57-a280-f7baa9b69a53.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/7d5b50d9-3ed9-47db-915a-72ee54ca5a4d.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/528052d3-d196-4113-8959-895ec9cba3b0.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/8db66c01-4f67-4046-9667-c8eea5e42906.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/bcc8eef6-dfec-4a25-af85-ecb4382d4189.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/10f9bb3d-75c2-41ee-889f-e779bfef6eda.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/29f1ee16-cc9a-4223-9744-b3fef9d3fc49.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/fa6ed143-ff01-4441-8856-7910edd31df1.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/a90c6b90-3758-432d-a02a-14b5312b7eb5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/cfd39904-bb30-4ca1-8c31-4d5021e90b34.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/listicle/bc3ded7e-d784-40c3-b128-0f83e8cc0984.webp"
    ],
  },
  {
    id: "how-to",
    label: "How To",
    description: "Step-by-step explainer that teaches a quick win or product application.",
    imageTypeId: "how-to",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/3d917d14-0aeb-4c7d-a8b6-2865322243e3.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/a3a5826a-0c61-47eb-92e4-746b577f6ca3.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/9f16306b-1aac-4635-b522-a924c98470a7.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/0c41c1a0-cb78-482c-bdb1-91bd453ab801.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/7c93d278-a341-4ba4-ba4e-bd6a216d28a2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/9ea21a79-7398-4fe5-a033-41ab0bf9a663.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/5952de67-0144-4f5f-b722-dd0e3bfaade9.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/ac246f47-0663-472e-a4fb-fba78f6145e5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/77371c02-b1f7-4234-87af-fca628cd52ec.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/a2e2dc37-e9d0-4f2c-a4fb-bc2cf1c8ac52.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/how-to/762697df-0633-4f99-a717-4e19ae80adb2.webp"
    ],
  },
  {
    id: "infographic",
    label: "Infographic",
    description: "Data-led layout using stats, icons, and small charts to make a credibility-first claim.",
    imageTypeId: "infographic",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/94a5cc14-dcff-4228-8242-be310428ffd7.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/b6c422e2-bf96-4401-8a87-fa3d77612932.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/1a269f9b-c17e-48bf-969b-28640fbb6143.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/0972b696-0d1d-43c6-a581-507d64fc1e56.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/31385ad3-7d1a-43c9-98eb-eae8c53e9c26.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/f4208a26-6fe5-468f-9cb0-f653420ae703.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/infographic/9223827a-a08a-4ec7-b893-7abb693c157d.webp"
    ],
  },
  {
    id: "split-screen",
    label: "Split Screen",
    description: "Two-panel composition contrasting two states, audiences, or product modes for instant scanning.",
    imageTypeId: "split-screen",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/7b7906fe-876c-40b5-861a-b78dde983b67.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/8668bd40-31c3-4bd5-a67f-90808fe6f91c.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/5a551c0d-d385-4711-bb71-765122c230fd.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/db1f45fa-da3a-415c-b69d-794d95816cb6.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/ff13a988-034c-40e0-ad3f-4c3e5539e34c.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/split-screen/cc509a67-993a-4421-9e08-e4bdfc7f9087.webp"
    ],
  },
  {
    id: "case-study",
    label: "Case Study",
    description: "Customer-result story with the headline outcome, light copy, and a credible before/after cue.",
    imageTypeId: "case-study",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/65d6ffee-1ca7-4e6b-a3f1-5b9df8dd4676.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/27ed7ccd-48d3-4ef1-915b-650094797fbe.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/0465c408-df8c-42ec-8a2a-5cca8f7e15aa.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/66555f74-b074-42b2-be22-43446afc722a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/08eebea1-88fc-4ec2-a669-cafb6ff19fc4.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/775423e7-c7d9-4d51-b7e0-2ce234d07a48.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/35fd5624-b33d-4db6-bb97-61fe4734c263.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/f1e7b19e-be6d-4050-bc81-9f7e95a9eac5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/ed826dd8-fbaf-4325-aac2-76396b0a0811.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/case-study/41c2de53-a603-4369-9898-81a6b4542f8f.webp"
    ],
  },
  {
    id: "review",
    label: "Review",
    description: "Star ratings, customer quotes, and review-card framing built for retargeting and trust building.",
    imageTypeId: "review",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/937f13ca-414c-4485-a28b-f8868e8a5d02.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/251de6d7-d68e-457c-a254-1cb91f88aed2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/7e717de8-78a8-4eb4-afe4-110d00c7e417.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/27228cdf-1b59-410b-ae17-f04386694eb0.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/947e44d5-ff70-494b-9306-ce2a8f237c5e.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/d8796bfe-33d3-43f4-bc33-64256381f38b.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/46381dbf-c086-40f8-8a74-503206cfd30d.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/62955712-4513-456d-a30f-a1caeb38a2eb.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/fb727148-d02f-4b1b-8d91-4a06e58ba391.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/8b7a750e-3a8e-4f58-a5cf-b120c0c641e2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/ae0d5933-628f-4ebe-bbce-9fc90999c313.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/dbb3baa8-e772-40e3-860e-3a54031d62a5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/c17b8161-9070-46c9-957b-afcedb170f79.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/review/400e2664-5cc5-4e72-930c-c12ccc429cd6.webp"
    ],
  },
  {
    id: "feature-benefit-pointout",
    label: "Feature Benefit Pointout",
    description: "Hero product with annotated callouts pointing to specific features and benefits.",
    imageTypeId: "feature-benefit-pointout",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/751d7a38-f86c-445a-83aa-cf24ecf4167a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/f62b681b-25d4-417e-8a2b-85d5b72d4beb.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/7ce0e7c2-105d-4cfb-9e31-3c03a6b780e8.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/90738222-8338-4aec-806d-04a29cd2fd6a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/4e6913da-16a9-45ca-9532-bf43a8416e1a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/ab4526a1-f92e-4f76-8578-95bde7d54848.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/b709f59c-e18f-4a92-9b4c-59631bfb2787.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/add553f8-0a80-49ec-a3d2-a3a3b8dd0891.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/df989548-cb4e-4c24-a741-ff76858b3bf2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/feature-benefit-pointout/f81776c9-5777-43ec-a675-7e4c0cbb9305.webp"
    ],
  },
  {
    id: "headline",
    label: "Headline",
    description: "Bold typographic ad where one strong headline carries the whole hook.",
    imageTypeId: "headline",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/d2abd373-0ede-4b6d-8869-62be4e27acbe.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/561dad1e-4ddb-4980-8927-20bfa11ea3cf.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/15b43340-0ac5-49a5-88cc-cd26b9745f2c.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/9de497f3-01ab-4e46-81a4-af1356ebf1e7.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/d2efba82-f737-46e9-9296-a5047e510cc4.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/b99d004a-2b86-402d-93f9-e2a1d41c4a8d.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/headline/a486d3a2-a25d-404a-a5be-ded38c1646c7.webp"
    ],
  },
  {
    id: "offer",
    label: "Offer",
    description: "Promo-led layout with the discount, badge, or deadline as the dominant visual element.",
    imageTypeId: "offer",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/ac33abd1-cdd0-4ab2-b175-6e580fc69f0b.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/1b0f177c-a81c-4a8a-89eb-671cffa26c3b.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/095146f9-25f0-4474-a1e4-7391f463c0e2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/93b3c810-3039-4498-b652-a2253b6f9d83.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/85924f2c-e161-4d9d-b1cf-1b6be6c68a43.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/adf5dce8-e6f4-4794-ace4-847f21a1e979.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/2209624c-ca19-4e3a-8c42-a06c81e4ed99.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/3fa23115-da60-4feb-ad02-911d9ecf6ffa.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/ffb02c56-32ea-4f4a-b366-7da53102d409.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/102e81b2-8eaa-42fd-ae5b-f2972565a3cc.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/8ac02981-88ec-42c0-a0bd-f01b0db852f5.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/offer/2b0c669e-060b-4826-b582-79d42f5144fc.webp"
    ],
  },
  {
    id: "ugc-overlay",
    label: "UGC overlay",
    description: "Creator-style still with overlaid hook text, designed to feel native to social feeds.",
    imageTypeId: "ugc-overlay",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/f5e5b480-814e-4ce9-a56b-f028b3060e5e.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/d348ea31-ea64-4bcd-9a5a-04c8695df889.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/137b266f-9e40-4c1c-8959-a1cb188f6cbf.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/08bd717a-b8c3-4d41-817a-c50817e208da.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/3665937c-ee9f-4ea7-8277-dc73e2ee15b8.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/61e6b0a9-0c14-46f7-8b00-9c0951ca89e3.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/f7443af1-29e8-4d7f-9cdd-4f8c0e75a9be.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/e4f7b16f-6242-4b6c-8508-c6bd087c8358.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/a5f1bac5-d441-48ca-8c56-9519ed7a3524.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/5fdd6916-e7f0-4e44-83fd-204353873b3a.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/5e099d7f-19b8-4c56-932b-bf9cb899f6e4.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/d9542eec-9c72-4d36-b87b-bfef3d5be486.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/bcdca6ac-951e-438b-8030-65f4546cb48c.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/511e5240-bf59-4f99-8207-a0e5d8e85994.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/feef4943-623e-464b-a2ac-cabbc6f20333.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/f46faed2-c575-4bef-8ba6-6f9c8df0af78.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/b1f74807-4550-47f3-b915-a31cca6dc894.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/94bf09ec-2918-416c-8e44-49d442070964.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/ugc-overlay/8343bb4e-4d21-42c9-aa1c-548e6d2a7c12.webp"
    ],
  },
  {
    id: "interface",
    label: "Interface",
    description: "Phone, browser, or in-app screenshot framing the product as software in action.",
    imageTypeId: "interface",
    images: [
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/800d1343-efce-4f63-a49b-b54afa117e40.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/580e3d6f-f52e-438a-bb3c-ed340ef59b30.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/da7400f9-524c-4441-910e-d08f46027e60.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/303f0cdb-feda-4670-9696-0f8bf03aebce.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/91f65f2d-2a6e-40c0-a744-b92ac9709fc7.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/9244d723-114d-4658-83de-b195acda0fe2.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/90ab234c-c8c0-4584-a296-fae0e305b665.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/054638cb-7918-4448-8f21-029289e6cd5c.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/27231446-171c-4ee1-91d0-fb6c9e5b5b10.webp",
      "https://cdn.wiserstudio.ai/static/ad-image-library/interface/cc860908-8361-40a6-bd37-83fca78f4d03.webp"
    ],
  },
] as const satisfies readonly ImageAdLibraryCategory[];

export const IMAGE_AD_LIBRARY: readonly ImageAdLibraryCategory[] =
  IMAGE_AD_LIBRARY_SOURCE.filter((cat) => !HIDDEN_IMAGE_AD_LIBRARY_CATEGORY_IDS.has(cat.id));

export interface FlatLibraryEntry {
  categoryId: string;
  categoryLabel: string;
  imageTypeId: ProductImageAdTypeId;
  url: string;
}

/** Pre-flattened list for quick filtering — order preserved from category order. */
export const IMAGE_AD_LIBRARY_FLAT: readonly FlatLibraryEntry[] =
  IMAGE_AD_LIBRARY.flatMap((cat) =>
    cat.images.map((url) => ({
      categoryId: cat.id,
      categoryLabel: cat.label,
      imageTypeId: cat.imageTypeId,
      url,
    })),
  );
